<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import UserResource from '@/resources/user'
import CalendarResource from '@/resources/calendar'
import ReservationCheckoutResource from '@/resources/reservationCheckout'
import { createCountdown } from '@/util/timers'
import ApexClubPitch from '../components/ApexClubPitch.vue'
import ApexClubDetail from '../components/ApexClubDetail.vue'

export default {
    components: {
        ApexClubPitch,
        ApexClubDetail
    },
    computed: {
        ...mapGetters(['userResource', 'isApexClub']),
        calendarId() {
            return this.$route.params.calendarId
        },
        upcomingReservationResources() {
            if (!this.upcomingReservationsPageData) {
                return []
            }

            return this.upcomingReservationsPageData.resources
        },
        seatedUserID() {
            return this.seated?.current?.user
        },
        seatedCanProceed() {
            if (this.seatedCanProceedOverride) {
                return true
            }

            return this.seatedUserID == this.userResource.id
        },
        userHasEarlyEntry() {
            if (this.seated?.next?.user != this.userResource.id) {
                return false
            }

            const earlyEntranceDate = (this.seated.next?.user == this.userResource.id) ? moment(this.seated.next?.time).subtract(20, 'minutes') : null
            return earlyEntranceDate && !this.seated.current && this.isApexClub && (moment() > earlyEntranceDate)
        }
    },
    data() {
        return {
            loading: true,
            calendarResource: null,
            checkoutResource: null,
            upcomingReservationsPageData: null,
            seatedCanProceedOverride: false,
            seated: null,
            intervalIDSeated: null,
            countdown: null,
            statusCodes: {
                locked: {
                    text: 'This simulator is locked. Click "Book Now" to unlock',
                    variant: 'danger'
                },
                inUse: {
                    text: 'Someone else is using this simulator. Click "Book Now" to reserve',
                    variant: 'danger'
                },
                partyDisbanded: {
                    text: 'Party has been disbanded',
                    variant: 'info'
                },
            }
        }
    },
    methods: {
        async load() {
            this.calendarResource = await CalendarResource.detail(this.calendarId, { resolveRelated: true })
            this.upcomingReservationsPageData = await UserResource.getReservationsFuture()
            const session = this.$route.query.session || null

            if (this.$route.query.checkout) {
                this.checkoutResource = await ReservationCheckoutResource.detail(this.$route.query.checkout)
            }

            if (this.$route.query.statusCode) {
                if (this.$route.query.statusCode == 'paymentLinkSuccess') {
                    const newQuery = { ...this.$route.query }
                    delete newQuery.statusCode
                    delete newQuery.checkout
                    const value = (this.checkoutResource?.attributes.checkout_data.total_cents) ? this.checkoutResource.attributes.checkout_data.total_cents / 100 : null
                    this.$analytics.track('checkout_success', { value, session, method: 'payment_link', currency: 'USD' })
                    this.$router.replace({ query: newQuery })
                } else {
                    const message = this.statusCodes[this.$route.query.statusCode]
                    this.addMessage(message.variant || 'danger', message.text || 'An unknown error occured')
                }
            }

            await this.fetchSeated()
            this.intervalIDSeated = setInterval(() => this.fetchSeated(), 1e4)
            this.loading = false
        },
        async fetchSeated() {
            this.seated = await this.calendarResource.seated()
            console.log(this.seated)

            if (this.seated?.next?.time && this.seated?.next?.user == this.userResource.id) {
                const timer = createCountdown(this.seated.next.time)

                const countdown = {
                    hours: null,
                    mins: null,
                    secs: null,
                    stop: timer.stop
                }

                timer.emitter.on('tick', (payload) => {
                    if (!payload) {
                        this.countdown = null
                        timer.stop()
                        return null
                    }

                    countdown.hours = payload.hours
                    countdown.mins = payload.mins
                    countdown.secs = payload.secs
                })

                this.countdown = countdown
                timer.start()
            } else {
                this.countdown = null
            }
        },
        onApexClubRegister() {
            this.$refs.apexClubModal.hide()
        }
    },
    async mounted() {
        await this.load()
    },
    beforeDestroy() {
        if (this.intervalIDSeated) {
            clearInterval(this.intervalIDSeated)
        }

        if (this.countdown) {
            this.countdown.stop()
        }
    }
}
</script>

<template>
    <div>
        <b-container v-if="!loading">
            <b-row class="mt-3">
                <b-col md="8" offset-md="2">
                    <messages />
                    
                    <b-card v-if="seatedCanProceed" class="my-3">
                        <h4 class="text-center">"{{ calendarResource }}" est prêt pour vous !</h4>
                        <p class="text-center">Veuillez vous diriger et vous asseoir</p>
                        <hr />
                        <p class="text-center text-muted">Configurez votre course sur l'écran de lancement ou en cliquant sur "Allons-y" ci-dessous.</p>
                        <p class="text-center"><b-button :to="{ name: 'calendarControl', params: { calendarId: $route.params.calendarId }, query: { loginUser: userResource.id } }" variant="secondary">Allons-y !</b-button></p>
                    </b-card>

                    <b-card v-else-if="countdown">
                        <p class="text-center">Votre prochaine session commence dans</p>
                        <div class="hms text-center">
                            {{ countdown.hours }}:{{ countdown.mins }}:{{ countdown.secs }}
                        </div>
                        <div v-if="isApexClub && seated.next && seated.next.user == userResource.id">
                            <div class="text-center mb-2">
                                <b-badge variant="success"><b-icon icon="check" /> {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</b-badge>
                            </div>
                            <p class="text-center text-muted">Vous êtes dedans ! Vous bénéficiez d'un accès anticipé !</p>
                            <b-button @click="seatedCanProceedOverride = true" :disabled="!userHasEarlyEntry" class="col-sm-12">
                                Laissez-moi entrer tôt !
                            </b-button>
                        </div>
                        <div v-else class="text-center">
                            <hr />
                            <p><strong>Les membres de {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} peuvent débloquer les simulateurs plus tôt !</strong> Les membres de {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} économisent également 20% sur toutes leurs courses et ont accès à des courses exclusives !</p>
                            <b-button @click="$refs.apexClubModal.show()">Rejoindre l'Apex Club</b-button>
                        </div>
                    </b-card>

                    <div v-else>
                        <p class="text-muted text-center">Revenez ici dans l'heure précédant votre prochaine réservation sur ce simulateur ("{{ calendarResource }}").</p>
                        <b-card header="Réservations à venir">
                            <b-table-simple v-if="upcomingReservationResources.length > 0">
                                <b-tr>
                                    <b-th>Date</b-th>
                                    <b-th>Simulateur</b-th>
                                    <b-th>&nbsp;</b-th>
                                </b-tr>

                                <b-tr v-for="(reserv, i) in upcomingReservationResources" :key="i">
                                    <b-td>{{ [reserv.getDateFormatted(), reserv.getTimeFormatted()].join(' @ ') }}</b-td>
                                    <b-td>{{ reserv.attributes.calendar_name }}</b-td>
                                    <b-td><a :href="reserv.getAddToCalendarLink()"><b-icon icon="calendar-plus" /> Ajouter au calendrier</a></b-td>
                                </b-tr>
                            </b-table-simple>

                            <p class="text-center text-muted" v-else>
                                Aucune réservation à venir <br />
                                <b-button variant="secondary" :to="{ name: 'reservationsSingle', params: { calendarId } }" class="mt-3">Réservez maintenant !</b-button>
                            </p>
                        </b-card>
                    </div>

                    <b-card class="mt-3 text-center">
                        <p>Préparez-vous pour la course à votre façon. <strong>Personnalisez votre voiture, votre circuit, vos assistances et vos paramètres de session</strong> avant de prendre le départ. Que vous souhaitiez une croisière décontractée ou un défi compétitif, ajustez votre expérience à tout moment avant le début de la course.</p>
                        <b-button variant="primary" :to="{ name: 'racesPresets' }"><b-icon icon="collection-play" /> Personnalisez votre course</b-button>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-modal ref="apexClubModal" :title="$appConfig('APEX_CLUB_NAME', 'Apex Club') + ' Inscription'" size="md" hide-footer no-fade centered>
            <apex-club-pitch @success="onApexClubRegister">
                <p class="text-muted"><small>(Oui, vous pouvez utiliser vos sessions pour vos amis et votre famille !) — <a href="javascript:{}" v-b-toggle.collapse-faq>Plus d'infos &amp; FAQ</a></small></p>
                <b-collapse id="collapse-faq" class="mb-3">
                    <b-card>
                        <apex-club-detail />
                    </b-card>
                </b-collapse>
            </apex-club-pitch>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.hms {
    font-size: 60px;
}
</style>
